import { getEnv } from 'helper/window-helper';
import { hasAdminEquivalentRole } from 'helper/role-helper';
import { RestCall } from 'service/api/rest/rest-call';

/** Rota da tela NOVA de aprovação de pedidos (só empresas com o fluxo habilitado). */
export const ROUTE_FLUXO_APROVACAO_NOVO = '/fluxo-aprovacao-pedido';

/** Rota da tela LEGADA de pedidos (só empresas sem o novo fluxo). */
export const ROUTE_PEDIDOS_LEGADO = '/pedidos';

async function readFluxoAprovacaoCampo(
  companyId: number | null | undefined,
  campo: 'fluxoAprovacao' | 'fluxoAprovacaoCompleto'
): Promise<boolean | null> {
  if (companyId === null || companyId === undefined) {
    return null;
  }
  const endpoint = getEnv('REACT_APP_END_POINT_CONFIGURATION');
  try {
    const response = await new RestCall(`${endpoint}/fluxo-aprovacao/${companyId}`).GetById();
    if (response?.success) {
      return Boolean(response.data?.[campo]);
    }
    return null;
  } catch {
    return null;
  }
}

export function readFluxoAprovacaoFlag(companyId?: number | null): Promise<boolean | null> {
  return readFluxoAprovacaoCampo(companyId, 'fluxoAprovacao');
}

export function readFluxoAprovacaoCompletoFlag(companyId?: number | null): Promise<boolean | null> {
  return readFluxoAprovacaoCampo(companyId, 'fluxoAprovacaoCompleto');
}

/**
 * Regra LEGADA do botão (flag CON_BLN_FLUXO_APROVACAO): quem tem perfil equivalente a admin
 * gera/envia direto, COMPRAS_GESTOR também, e os demais solicitam aprovação. O conjunto de
 * perfis equivalentes a admin vem de `hasAdminEquivalentRole` (ADMIN, CONSULTOR, SUPORTE e
 * DESENVOLVEDOR) — não basta testar 'ADMIN', senão os três perfis criados no desmembramento do
 * admin cairiam no caminho de "solicitar" e perderiam a geração direta que sempre tiveram.
 */
export function resolverFluxoAprovacaoLegado(
  fluxoAprovacaoLegado: boolean | null | undefined,
  roles: string[] | null | undefined
): boolean | null {
  if (hasAdminEquivalentRole(roles)) {
    return false;
  }
  if (fluxoAprovacaoLegado === null || fluxoAprovacaoLegado === undefined) {
    return null;
  }
  return fluxoAprovacaoLegado && !roles?.includes('COMPRAS_GESTOR');
}

export function resolverFluxoAprovacaoBotao(
  configuracao: { fluxoAprovacao?: boolean | null; fluxoAprovacaoCompleto?: boolean | null } | null | undefined,
  roles: string[] | null | undefined
): boolean | null {
  if (configuracao?.fluxoAprovacaoCompleto === true) {
    return true;
  }
  return resolverFluxoAprovacaoLegado(configuracao?.fluxoAprovacao, roles);
}

const MENSAGEM_ERRO_CONFIGURACAO_EMPRESA = 'Ops!!! Algo deu errado tente novamente mais tarde...';

export interface ConfiguracaoFluxoAprovacao {
  success: boolean;
  message: string;
  fluxoAprovacaoCompleto: boolean | null;
  fluxoAprovacao: boolean | null;
}

export async function buscarConfiguracaoFluxoAprovacao(
  companyId: number | null | undefined,
  roles: string[] | null | undefined
): Promise<ConfiguracaoFluxoAprovacao> {
  if (companyId === null || companyId === undefined) {
    return { success: true, message: '', fluxoAprovacaoCompleto: null, fluxoAprovacao: null };
  }
  const endpoint = getEnv('REACT_APP_END_POINT_CONFIGURATION');
  try {
    const response = await new RestCall(`${endpoint}/fluxo-aprovacao/${companyId}`).GetById();
    if (response?.success) {
      return {
        success: true,
        message: '',
        fluxoAprovacaoCompleto: Boolean(response.data?.fluxoAprovacaoCompleto),
        fluxoAprovacao: resolverFluxoAprovacaoBotao(response.data, roles),
      };
    }
    return {
      success: false,
      message: response?.message ?? MENSAGEM_ERRO_CONFIGURACAO_EMPRESA,
      fluxoAprovacaoCompleto: null,
      fluxoAprovacao: null,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.message ?? MENSAGEM_ERRO_CONFIGURACAO_EMPRESA,
      fluxoAprovacaoCompleto: null,
      fluxoAprovacao: null,
    };
  }
}

/**
 * Por empresa, o usuário vê SÓ um dos itens de pedido no menu, e os dois dependem de UMA
 * única flag — CON_BLN_FLUXO_APROVACAO_COMPLETO (o fluxo novo é opt-in explícito):
 * - '/fluxo-aprovacao-pedido' (novo) aparece quando a flag está habilitada (true);
 * - '/pedidos' (legado) aparece quando a flag está desabilitada (false), pois a tela legada é a
 *   alternativa a ela — não depende da flag legada CON_BLN_FLUXO_APROVACAO, que governa o
 *   comportamento de aprovação DENTRO do fluxo anterior, não a existência da tela.
 * Enquanto a flag não resolve (null: carregando/erro/sem empresa) nenhum dos dois aparece,
 * garantindo que nunca surjam os dois juntos. Os demais itens não sofrem gate por flag e mantêm
 * apenas a visibilidade por perfil (`baseVisivel`).
 */
export function itemMenuVisivelPorFluxo(
  route: string,
  baseVisivel: boolean,
  flag: boolean | null
): boolean {
  if (!baseVisivel) {
    return false;
  }
  if (route === ROUTE_FLUXO_APROVACAO_NOVO) {
    return flag === true;
  }
  if (route === ROUTE_PEDIDOS_LEGADO) {
    return flag === false;
  }
  return true;
}
